import {
  AccountType,
  PermissionKey,
  UserPlanScope,
  UserPlanType,
} from '@pt/enums/user'
import type { Module, MutationTree, ActionTree, GetterTree } from 'vuex'
import { getAdvUserInfo, getUserPartnerInfo } from '@pt/api/user'
import { logout as ptLogout } from '@pt/utils/auth'
import {
  isInternalAccount,
  getSubscribeStatus,
  isSubscribeExpired,
  isRecharge,
  hasPlanInEffect,
} from './helper'
import type { MakerStatus } from './helper'
import type { UserInfo } from '@pt/types/user.type'
import { LogoutLogOption } from '@pt/utils/log'
import { UserPartnerResponse } from '@pt/api/user/type'
import { PartnerApproveStatus, PartnerCategory } from '@pt/enums/partner'
import { PlanType } from '@/common/permission'
import { isChinaIp } from '@com/utils/redirect-to-cn'
import * as Sentry from '@sentry/vue'
import { isProduction, isPrerelease } from '@com/config/env.js'

export interface UserState {
  roles: string[]
  info: UserInfo & {
    is_internal_account: boolean
    is_login_from_ss: boolean
    is_login_from_pt: boolean
    is_login_from_wh: boolean

    // 可玩套餐
    is_subscribe: boolean
    is_subscribe_expired: boolean

    is_allow_gm: 2 | 1
    is_allow_ps: 2 | 1
    // 余额
    balance: number
    // 余额阈值
    balance_threshold: number
    // 是否支持信用支付
    support_credit: boolean
    // 最低余额
    min_amount: number
  }
  partnerInfo: UserPartnerResponse
}

const state: UserState = {
  roles: [],
  info: {
    username: '',
    account_type: AccountType.Normal,
    adm_id: 0, // 管理员登录的，adm_id>0
    email: '',
    id: 0,
    is_agree_privacy_agreement: false,
    login_from: 1,
    permissions: [],
    system_time: 0,
    // 套餐数据
    plans: null,
    // 而外新增的属性
    is_internal_account: false,
    is_login_from_ss: false,
    is_login_from_pt: false,
    is_login_from_wh: false,

    // 可玩套餐
    is_subscribe: false,
    is_subscribe_expired: false,

    is_allow_gm: 2,
    is_allow_ps: 2,
    balance: 0,
    balance_threshold: 100,
    support_credit: false,
    min_amount: 0,

    recharge_stop_bill_method: '',
    channel_ids: [],
  },
  partnerInfo: {
    list: [],
    category: PartnerCategory.Unapproved,
    approve_status: PartnerApproveStatus.Unreviewed,
  },
}

const getters: GetterTree<UserState, unknown> = {
  // 可玩套餐是否试用
  isPlayableTrial(state) {
    const status = getSubscribeStatus(state.info, [
      UserPlanScope.Playable,
      UserPlanScope.PlayableAndVideo,
    ])

    return status.type === UserPlanType.Trial
  },

  gm(state): MakerStatus {
    return getSubscribeStatus(
      state.info,
      [UserPlanScope.Playable, UserPlanScope.PlayableAndVideo],
      PermissionKey.Gm,
    )
  },

  dm(state): MakerStatus {
    return getSubscribeStatus(
      state.info,
      [UserPlanScope.Playable, UserPlanScope.PlayableAndVideo],
      PermissionKey.Dm,
    )
  },

  ve(state): MakerStatus {
    return getSubscribeStatus(
      state.info,
      [UserPlanScope.Video, UserPlanScope.PlayableAndVideo],
      PermissionKey.Ve,
    )
  },

  vrec(state): MakerStatus {
    return getSubscribeStatus(
      state.info,
      [UserPlanScope.Video, UserPlanScope.PlayableAndVideo],
      PermissionKey.Vrec,
    )
  },

  /**
   * 是否订阅 & 订阅是否过期了
   * 不管是付费的正式套餐还是免费的试用套餐，只要有一个未过期，就算订阅了
   */
  isSubscribe(state): boolean {
    return (
      state.info.plans?.some((plan) => !isSubscribeExpired(state.info, plan)) ??
      false
    )
  },

  /**
   * 判断订阅是否过期了
   * 每个制作页面都要验证这个信息
   * 工具跳转到列表页面，到地址要看下是否有问题
   *
   * @returns boolean true 过期 false 未过期
   */
  // isSubscribeExpired(state): boolean {
  //   return false
  //   // return plan.expired_time <= userInfo.system_time
  // },

  /** 是否开通计费 */
  isRecharge(state): boolean {
    return isRecharge(state.info)
  },

  /** 是否开通订阅，且在有效期内 */
  isSubscription(state): boolean {
    return hasPlanInEffect(
      PlanType.Subscription,
      state.info.plans ?? [],
      state.info,
    )
  },

  /** 是否开通按量的套餐 */
  isRation(state): boolean {
    return hasPlanInEffect(PlanType.Ration, state.info.plans ?? [], state.info)
  },

  /** 是否达到阈值 */
  isReachBalanceThreshold(state): boolean {
    // 账户余额<=阈值
    return isRecharge(state.info) && state.info.balance <= state.info.min_amount
  },

  /** 导航栏账户余额不足提醒 */
  isInsufficientBalance(state): boolean {
    // 账户余额<最低余额+100或账户余额<100
    return (
      isRecharge(state.info) &&
      (state.info.balance < state.info.min_amount + 100 ||
        state.info.balance < 100)
    )
    // 是否支持信用支付
  },
}

const mutations: MutationTree<UserState> = {
  updateRoles(state, roles: UserState['roles'] = []) {
    // 至少有一个 default 页面
    state.roles = roles.concat(['default'])
  },

  updateInfo(state, info: UserState['info']) {
    state.info = info
  },

  updatePartnerInfo(state, info: UserPartnerResponse) {
    state.partnerInfo = info
  },
}

export const actions: ActionTree<UserState, unknown> = {
  async GetUserInfo({ commit, state }) {
    const res = await getAdvUserInfo()
    const userInfo = res.data.data as UserInfo
    if (window.posthog && typeof window.posthog.identify === 'function') {
      window.posthog.identify(
        String(userInfo.id), // Required. Replace 'distinct_id' with your user's unique identifier
        {
          email: userInfo.email,
          name: userInfo.username,
          env: process.env.NODE_ENV,
        }, // $set, optional
      )
    }

    if (isProduction || isPrerelease) {
      Sentry.setUser({
        id: String(userInfo.id),
        email: userInfo.email,
      })
    }

    const isFromSS = userInfo.login_from === 3
    const isFromWh = userInfo.login_from === 2 // 从素材仓库后台登陆的，wh 是 warehouse 的缩写
    const isFormPt = userInfo.login_from === 1

    const permissions = userInfo.permissions || []

    // if (!isChinaIp(userInfo.cc)) {
    //   permissions.push(PermissionKey.International)
    // }
    // 非中国地区放开视频工具使用权限
    permissions.push(PermissionKey.International)

    if (isInternalAccount(userInfo)) {
      permissions.push(PermissionKey.InternalAccount)
      if (permissions.includes(PermissionKey.VeTpl)) {
        permissions.push(PermissionKey.InternalTpl)
      }
    }
    // 调整为国内、国际都可以在线购买套餐
    // 不是国内域名可在线购买套餐
    // if (!isCNSuffix()) {
    //   permissions.push(PermissionKey.GrantPlan)
    // }

    commit('updateRoles', permissions)

    const isAllow = (key: PermissionKey): boolean => {
      return permissions.includes(key)
    }

    const info: UserState['info'] = {
      ...userInfo,
      is_internal_account: isInternalAccount(userInfo),
      is_login_from_ss: isFromSS,
      is_login_from_pt: isFormPt,
      is_login_from_wh: isFromWh,
      is_subscribe: false,
      is_subscribe_expired: false,

      is_allow_gm: isAllow(PermissionKey.Gm) ? 1 : 2,
      is_allow_ps: isAllow(PermissionKey.Ps) ? 1 : 2,
    }

    const status = getSubscribeStatus(info, [
      UserPlanScope.Playable,
      UserPlanScope.PlayableAndVideo,
    ])

    info.is_subscribe = status.subscribed
    info.is_subscribe_expired = status.expired
    commit('updateInfo', info)
    return state.info
  },

  LogOut(context, option?: LogoutLogOption) {
    return ptLogout(option)
  },

  async GetUserPartnerInfo({ commit }) {
    const res = await getUserPartnerInfo()
    if (res.data.code === 0) {
      commit('updatePartnerInfo', res.data.data)
    }
  },
}

const user: Module<UserState, unknown> = {
  state,
  getters,
  mutations,
  actions,
}

export default user
